/* Входы формы «Карусели для поста» ⇄ состояние движка шаблона (план вехи 3, шаг 2; collage-slides §2.2). Чистые функции без DOM:
   viewOf — что показать в S по входам сервиса (повтор из «Истории», «Изменить», заявка родной формы), inputsOf — входы из S (зеркало
   inputs-mirror, точки записи — syncFoot и renderCar движка). Ключи и кодирование — те же, что пишет родная форма (SlideOrderForm.tsx
   kind="post"): тема (topic или text), оформление (style_id или style — опубликованное значение: ключ шаблона «min» или номер «1»),
   количество слайдов строкой, формат и тон — только опубликованные значения. Слоты фото пишет мост загрузок (media-bridge): inputsOf
   их не трогает. */
import type { StudioForm } from '../studio/studio-api';
import { enumOf, photoSlots, POST_STYLES, postStyleKey, textOf, type Field } from '../slides/product-model';

/** Поля формы экрана (как SlideOrderForm kind="post") */
export interface PostFields {
  topic?: Field;
  style?: Field;
  slides?: Field;
  format?: Field;
  tone?: Field;
  photos: Field[];
}
export function postFields(form: StudioForm | null | undefined): PostFields {
  return {
    topic: textOf(form, 'topic'),
    style: enumOf(form, 'style'),
    slides: enumOf(form, 'slides'),
    format: enumOf(form, 'format'),
    tone: enumOf(form, 'tone'),
    photos: form ? photoSlots(form) : [],
  };
}

/** Ключ шаблона (min, dark…) опубликованного значения оформления: ключ или номер прототипа; незнакомое значение — undefined */
export const styleKeyOf = (value: string | undefined): string | undefined => (value && POST_STYLES[value] ? postStyleKey(value) : undefined);
/** Опубликованное значение оформления для ключа шаблона: ключ или номер прототипа — как его публикует форма */
export const styleValueOf = (field: Field | undefined, key: string | undefined): string | undefined =>
  field && key ? field.values.find(value => styleKeyOf(value) === key) : undefined;

/** Что экран держит в S: тема как есть, оформление (ключ шаблона), слайдов, формат, тон */
export interface PostView {
  text: string;
  sty?: string;
  n?: number;
  ar?: string;
  tone?: string;
}

/** Входы → вид S (только опубликованные поля и значения; неопубликованное не задано) */
export function viewOf(form: StudioForm | null | undefined, inputs: Record<string, string>): PostView {
  const f = postFields(form);
  const view: PostView = { text: f.topic ? (inputs[f.topic.key] ?? '') : '' };
  const pick = (field: Field | undefined) => {
    const value = field ? inputs[field.key] : undefined;
    return value !== undefined && field!.values.includes(value) ? value : undefined;
  };
  const sty = styleKeyOf(pick(f.style));
  if (sty) view.sty = sty;
  const n = pick(f.slides);
  if (n && Number.isSafeInteger(Number(n))) view.n = Number(n);
  const ar = pick(f.format);
  if (ar) view.ar = ar;
  const tone = pick(f.tone);
  if (tone) view.tone = tone;
  return view;
}

/** Вид S → входы поверх prev: опубликованные поля экрана; слоты фото — как в prev (их пишет мост). Пустая тема, которой в prev
    не было, не появляется (как у родной формы: ключ пишет только правка) */
export function inputsOf(form: StudioForm | null | undefined, view: PostView, prev: Record<string, string>): Record<string, string> {
  const f = postFields(form);
  const next = { ...prev };
  const put = (field: Field | undefined, value: string | undefined) => {
    if (!field || value === undefined) return;
    if (value === '' && next[field.key] === undefined) return;
    next[field.key] = value;
  };
  put(f.topic, view.text);
  put(f.style, styleValueOf(f.style, view.sty));
  if (f.slides && view.n !== undefined && f.slides.values.includes(String(view.n))) put(f.slides, String(view.n));
  if (f.format && view.ar && f.format.values.includes(view.ar)) put(f.format, view.ar);
  if (f.tone && view.tone && f.tone.values.includes(view.tone)) put(f.tone, view.tone);
  return next;
}

/** Вход заказа → вид S без формы (заявка до прихода формы, хвост после перезагрузки): поля по допустимым именам ключей (product-model KEYS) */
export function viewOfInputs(form: StudioForm | null | undefined, inputs: Record<string, unknown>): PostView {
  const strings = Object.fromEntries(Object.entries(inputs).filter((entry): entry is [string, string] => typeof entry[1] === 'string'));
  if (form) return viewOf(form, strings);
  const first = (keys: string[]) => keys.map(key => strings[key]).find(value => value !== undefined);
  const view: PostView = { text: first(['topic', 'text']) ?? '' };
  const sty = styleKeyOf(first(['style_id', 'style']));
  if (sty) view.sty = sty;
  const n = Number(first(['slides', 'slide_count', 'slides_count']));
  if (Number.isSafeInteger(n) && n > 0) view.n = n;
  const ar = first(['format']);
  if (ar) view.ar = ar;
  const tone = first(['tone']);
  if (tone) view.tone = tone;
  return view;
}

/** Фото заказа: id загрузок слотов фото в порядке слотов (photo_1…photo_10) */
export function photoIds(form: StudioForm | null | undefined, inputs: Record<string, unknown>): string[] {
  const keys = form ? postFields(form).photos.map(field => field.key) : Object.keys(inputs).filter(key => /^photo(_\d+)?$/.test(key));
  const index = (key: string) => Number(key.match(/(\d+)$/)?.[1] ?? 0);
  return keys
    .slice()
    .sort((a, b) => index(a) - index(b))
    .map(key => inputs[key])
    .filter((id): id is string => typeof id === 'string' && !!id);
}
